import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

/**
 * Auth state shared across the app — minimal, no provider-side login UX.
 *
 * Login flow lives in `AuthCallback.jsx` (which detects `#session_id=` in the
 * URL during render and trades it for a cookie via `POST /api/auth/session`).
 * This provider only OBSERVES the server's view of the session and exposes
 * `{user, loading, refresh, logout}` to the rest of the app.
 *
 * Race-condition note (per Emergent Auth playbook):
 * If the URL fragment contains `session_id=` we MUST skip the initial
 * `/auth/status` probe — AuthCallback runs first, exchanges the session,
 * THEN we refresh. Otherwise the probe races the cookie set and we'd
 * render "logged out" for a flash.
 */

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // `/auth/status` is soft — returns 200 with `authenticated:false` for
      // anonymous, so the network panel doesn't fill with 401s on every page.
      const { data } = await api.get("/auth/status", { withCredentials: true });
      setUser(data?.authenticated ? data.user : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", null, { withCredentials: true });
    } catch {
      // Server may already have invalidated the session — proceed to clear local state.
    }
    setUser(null);
  }, []);

  useEffect(() => {
    // CRITICAL: skip the initial probe when AuthCallback is about to run.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
